import { useEffect, useLayoutEffect, useState } from 'react'

import type { PhoneCountry } from '../../lib/phone/countries'
import { parseE164 } from '../../lib/phone/utils'

import { processAutofillValue } from './helpers'

// Parse value prop changes
export function useValueEffect(
  value: string | undefined,
  countries: PhoneCountry[],
  setSelectedCountry: React.Dispatch<React.SetStateAction<PhoneCountry>>,
  setNationalNumber: React.Dispatch<React.SetStateAction<string>>
) {
  useEffect(() => {
    if (value !== undefined) {
      const parsed = parseE164(value, countries)
      if (parsed.country) {
        setSelectedCountry(parsed.country)
        setNationalNumber(parsed.national)
      }
    }
  }, [value, countries, setSelectedCountry, setNationalNumber])
}

// Force update mask value when country or national number changes (for autofill)
export function useMaskUpdateEffect(
  selectedCountry: PhoneCountry,
  nationalNumber: string,
  inputRef: React.MutableRefObject<HTMLInputElement | null>
) {
  useEffect(() => {
    if (inputRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const maskInstance = (inputRef.current as any).mask
      if (maskInstance) {
        // Update mask pattern when country changes
        if (maskInstance.mask !== selectedCountry.mask) {
          maskInstance.updateOptions({ mask: selectedCountry.mask })
        }
        
        // Update value if national number is set and doesn't match
        if (nationalNumber && maskInstance.value !== nationalNumber) {
          maskInstance.value = nationalNumber
          maskInstance.updateValue()
        }
      }
    }
  }, [selectedCountry.iso2, selectedCountry.mask, nationalNumber, inputRef])
}

// Handle browser autofill - intercept before mask applies
export function useAutofillEffect(
  selectedCountry: PhoneCountry,
  countries: PhoneCountry[],
  isProcessingAutofillRef: React.MutableRefObject<boolean>,
  setSelectedCountry: React.Dispatch<React.SetStateAction<PhoneCountry>>,
  setNationalNumber: React.Dispatch<React.SetStateAction<string>>,
  onChange?: (value: string | null, meta: { countryIso2: string; callingCode: string; national: string; isValid: boolean }) => void,
  inputRef?: React.MutableRefObject<HTMLInputElement | null>
) {
  useEffect(() => {
    const inputElement = inputRef?.current
    if (!inputElement) return

    const processAutofillValueWrapper = (rawValue: string) => {
      return processAutofillValue(rawValue, selectedCountry, countries, isProcessingAutofillRef, setSelectedCountry, setNationalNumber, onChange, inputRef)
    }

    // Intercept beforeinput event - fires before the value is set
    const handleBeforeInput = (e: InputEvent) => {
      if (e.inputType === 'insertReplacementText' || e.data) {
        // This might be autofill
        const potentialValue = e.data || (e.target as HTMLInputElement).value || ''
        const digitsOnly = potentialValue.replace(/\D/g, '')
        
        if (digitsOnly.length > selectedCountry.nationalDigits) {
          // Try to process as full number
          if (processAutofillValueWrapper(potentialValue)) {
            e.preventDefault()
            e.stopPropagation()
            return false
          }
        }
      }
    }

    // Intercept input event in capture phase - before IMaskInput processes it
    const handleInputCapture = (e: Event) => {
      const target = e.target as HTMLInputElement
      const rawValue = target.value || ''
      const digitsOnly = rawValue.replace(/\D/g, '')
      
      // Check if this looks like autofill with country code
      if (digitsOnly.length > selectedCountry.nationalDigits && !isProcessingAutofillRef.current) {
        if (processAutofillValue(rawValue, selectedCountry, countries, isProcessingAutofillRef, setSelectedCountry, setNationalNumber, onChange, inputRef)) {
          e.preventDefault()
          e.stopImmediatePropagation()
          return false
        }
      }
    }

    // Also check on focus (autofill might happen on focus)
    const handleFocus = () => {
      // Delay to allow autofill to complete
      setTimeout(() => {
        if (inputElement) {
          const rawValue = inputElement.value || ''
          processAutofillValueWrapper(rawValue)
        }
      }, 100)
    }

    // Use MutationObserver to catch DOM changes from autofill
    const observer = new MutationObserver(() => {
      if (inputElement && !isProcessingAutofillRef.current) {
        const rawValue = inputElement.value || ''
        processAutofillValueWrapper(rawValue)
      }
    })
    
    // Add event listeners in capture phase to intercept before IMaskInput
    inputElement.addEventListener('beforeinput', handleBeforeInput as EventListener, true)
    inputElement.addEventListener('input', handleInputCapture, true)
    inputElement.addEventListener('focus', handleFocus, true)
    
    observer.observe(inputElement, {
      attributes: true,
      attributeFilter: ['value'],
      childList: false,
      subtree: false,
      characterData: true,
    })
    
    return () => {
      inputElement.removeEventListener('beforeinput', handleBeforeInput as EventListener, true)
      inputElement.removeEventListener('input', handleInputCapture, true)
      inputElement.removeEventListener('focus', handleFocus, true)
      observer.disconnect()
    }
  }, [selectedCountry, countries, isProcessingAutofillRef, setSelectedCountry, setNationalNumber, onChange, inputRef])
}

// Close dropdown on outside click
export function useClickOutsideEffect(
  isDropdownOpen: boolean,
  dropdownRef: React.RefObject<HTMLDivElement>,
  buttonRef: React.RefObject<HTMLButtonElement>,
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>
) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
        setSearchQuery('')
        setFocusedIndex(-1)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen, dropdownRef, buttonRef, setIsDropdownOpen, setSearchQuery, setFocusedIndex])
}

// Focus search input when dropdown opens
export function useSearchFocusEffect(
  isDropdownOpen: boolean,
  searchInputRef: React.RefObject<HTMLInputElement>
) {
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isDropdownOpen, searchInputRef])
}

export type PhoneDropdownPortalPosition = {
  top: number
  left: number
  width: number
  maxHeight: number
}

/** Fixed coordinates for country list portal (escapes overflow:hidden ancestors). */
export function useDropdownPortalPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>
): PhoneDropdownPortalPosition | null {
  const [position, setPosition] = useState<PhoneDropdownPortalPosition | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return undefined
    }

    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const gap = 4
      const top = rect.bottom + gap
      const maxHeight = Math.max(160, Math.min(320, window.innerHeight - top - gap))
      setPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight,
      })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchorRef, isOpen])

  return position
}
